/**
 * DuplicateAlertModal — the "Patient Already Exists" modal that appears
 * when the backend returns 409 while creating a patient.
 *
 * Behaviour per Viyora spec:
 *  - Shows every matching patient (up to 5) with the match reason
 *  - "Open existing patient"     — jumps to /app/patients/:id
 *  - "Update existing patient"   — same route, edit-mode intent (parent decides)
 *  - "This is a family member"   — explicit override toggle → parent retries POST with force=true
 *  - "Cancel"                    — dismiss
 */
import { X, User, Phone, Mail, Calendar as CalIcon, AlertTriangle, ArrowUpRight, Users } from "lucide-react";
import { Link } from "react-router-dom";

const REASON_LABEL = {
  phone:    "Matching mobile number",
  email:    "Matching email address",
  "name+dob": "Matching name + date of birth",
  admin_merge: "Merged by admin",
};
const REASON_TONE = {
  phone:      "bg-[#FDF3ED] text-[#8A3F1E] border-[#F2D2BC]",
  email:      "bg-[#EEF1F5] text-[#28527A] border-[#CFDAE6]",
  "name+dob": "bg-[#F5F0E4] text-[#7A5928] border-[#EAE0C4]",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return iso; }
};

export default function DuplicateAlertModal({
  open, matches = [], attempted = {},
  onCancel, onForceCreate, onOpenExisting,
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm grid place-items-center p-4"
         data-testid="duplicate-alert-modal"
         role="dialog"
         aria-modal="true"
         aria-labelledby="dupe-title">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="relative bg-gradient-to-br from-[#FDF3ED] to-[#F5F0E4] px-6 py-5">
          <button onClick={onCancel} data-testid="dupe-close"
                  className="absolute top-3 right-3 w-8 h-8 rounded-full hover:bg-white/60 grid place-items-center">
            <X className="w-4 h-4 text-ink" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-[#D06A42] text-white grid place-items-center shrink-0 shadow-sm">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-ink-mid">Duplicate detected</div>
              <h2 id="dupe-title" className="font-display text-2xl text-ink leading-tight">Patient already exists</h2>
            </div>
          </div>
          <p className="mt-3 text-sm text-ink-mid">
            A patient with{" "}
            {matches.length === 1 ? "this record" : `these ${matches.length} matching records`}{" "}
            already exists in your clinic. Please pick one instead of creating a duplicate.
          </p>
        </div>

        {/* Matches list */}
        <div className="max-h-[45vh] overflow-y-auto divide-y divide-[#EFEADC]">
          {matches.map((m) => (
            <div key={m.id} className="px-6 py-4" data-testid={`dupe-match-${m.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-ink truncate">{m.name}</span>
                    {(m.match_reasons || []).map((r) => (
                      <span key={r} className={`text-[10px] px-2 py-0.5 rounded-full border ${REASON_TONE[r] || "bg-slate-100 text-slate-600"}`}>
                        {REASON_LABEL[r] || r}
                      </span>
                    ))}
                  </div>
                  <div className="mt-1.5 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-ink-mid">
                    {m.phone && <span className="flex items-center gap-1.5"><Phone className="w-3 h-3" />{m.phone}</span>}
                    {m.email && <span className="flex items-center gap-1.5 truncate"><Mail className="w-3 h-3" />{m.email}</span>}
                    {m.dob && <span className="flex items-center gap-1.5"><CalIcon className="w-3 h-3" />DOB {fmtDate(m.dob)}</span>}
                    {m.created_at && <span className="flex items-center gap-1.5"><User className="w-3 h-3" />Since {fmtDate(m.created_at)}</span>}
                  </div>
                </div>
                <Link
                  to={`/app/patients/${m.id}`}
                  onClick={() => onOpenExisting?.(m)}
                  data-testid={`dupe-open-${m.id}`}
                  className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-pine text-linen text-xs font-medium hover:bg-[#264A3B] transition-colors"
                >
                  Open <ArrowUpRight className="w-3 h-3" />
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* Footer: family override + cancel */}
        <div className="px-6 py-5 bg-[#FBFAF6] border-t border-[#EFEADC]">
          <button
            data-testid="dupe-force-create"
            onClick={onForceCreate}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-[#E7DEC5] hover:border-pine/40 hover:bg-[#F5F0E3] text-sm text-ink font-medium transition-colors"
          >
            <Users className="w-4 h-4" />
            This is a different person sharing the phone — create anyway
          </button>
          <div className="mt-3 text-[11px] text-ink-soft text-center">
            The clinic admin will see this action in the audit trail.
          </div>
          <div className="mt-3 flex justify-end">
            <button data-testid="dupe-cancel" onClick={onCancel}
                    className="text-sm text-ink-mid hover:text-ink px-4 py-1.5">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
